import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { BoxIcon } from 'lucide-react';

import { SmileIcon } from '@/components/atoms/Icons/SmileIcon';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ReplayButton } from '@/components/atoms/ReplayButton/ReplayButton';
import { getIntegrationByType } from '@/components/organisms/Integrations';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { InsightRawValue, InsightValues, Log } from '@/shared/types/analytics';
import { Desk } from '@/shared/types/desk';
import { Insight } from '@/shared/types/insights';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { COLUMN_PRESETS } from '@/util/constants';
import { parseTimestamp } from '@/util/dateUtils';

import { createCsatColumns } from './csatColumns';
import {
  formatSessionDateTime,
  formatDuration,
  getSentimentDisplay,
  getUserDisplayName,
  getGoalPercentage,
} from './utils/sessionUtils';

const columnHelper = createColumnHelper<Log>();

/**
 * Renders the value of an insight based on its type
 */
function renderInsightValue(insight: InsightValues, insightType: string) {
  let value: InsightRawValue;
  if (typeof insight === 'object' && insight !== null) {
    value = insight.value;
  } else {
    value = insight;
  }
  const renderContent = () => {
    const Trigger = () => {
      if (value === null || value === undefined || value === '') {
        return <span>-</span>;
      }
      switch (insightType) {
        case 'boolean':
          return (
            <Badge type="secondary" flavour={value ? 'successful' : 'warning'}>
              {value ? 'true' : 'false'}
            </Badge>
          );
        case 'categorical':
          return <Badge type="secondary">{String(value)}</Badge>;
        case 'int':
        case 'number':
          return Number(value).toLocaleString();
        case 'datetime':
          return <div className="truncate">{new Date(String(value)).toLocaleString()}</div>;
        case 'phone_number': {
          if (typeof value !== 'object' || value === null || !('number' in value)) {
            return <div className="truncate">-</div>;
          }
          const phoneValue = value as { number: string; country_code?: string | null };
          const phoneNumber = phoneValue.country_code
            ? `+${phoneValue.country_code} ${phoneValue.number}`
            : phoneValue.number;
          return <div className="truncate">{phoneNumber}</div>;
        }
        case 'email':
        case 'string':
        default:
          return <div className="truncate">{String(value)}</div>;
      }
    };

    if (typeof insight === 'object' && insight !== null && insight?.reasoning) {
      return (
        <InfoTooltip content={insight.reasoning as string} size="auto" className="max-w-64">
          <div>
            <Trigger />
          </div>
        </InfoTooltip>
      );
    }
    return <Trigger />;
  };
  return renderContent();
}

/**
 * Creates dynamic columns for insights
 */
function createInsightColumns(insights: Insight[]) {
  if (!insights || insights.length === 0) {
    return [];
  }
  return insights.map((insight) =>
    columnHelper.accessor((row) => row.insights?.[insight.name], {
      id: `${insight.insight_id}`,
      header: () => insight.name,
      cell: ({ getValue }) => {
        const value = getValue();
        return renderInsightValue(value, insight.type);
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: insight.name },
    })
  );
}

/**
 * Creates column definitions for the sessions table
 *
 * System insight columns (goal, sentiment, AI CSAT, conversation summary) are free and always
 * rendered.
 *
 * @param allInsights - Custom insight definitions; each one becomes a column. Historical values
 * stay visible even when the brain's insights_enabled toggle is off.
 */
export function createSessionColumns(
  t: TFunction,
  onReplayClick: (session: Log) => void,
  desks?: Desk[],
  lastSeen?: string[],
  allInsights?: Insight[],
  onPrefetch?: (sessionId: string, startTime?: string) => void
) {
  return [
    // Review (Replay) action column - always visible, first column
    columnHelper.display({
      id: 'review',
      header: () => null,
      enableHiding: false,
      cell: ({ row }) => (
        <ReplayButton
          sessionId={row.original.session_id}
          lastSeen={lastSeen}
          onClick={() => onReplayClick(row.original)}
          onPrefetch={
            onPrefetch
              ? () => onPrefetch(row.original.session_id, row.original.start_time)
              : undefined
          }
        />
      ),
      size: 60,
      minSize: 48,
      maxSize: 72,
      meta: { className: 'text-center' },
    }),
    columnHelper.accessor((row) => row.user_name || row.user_id || '', {
      id: 'user',
      header: () => t('analytics.fields.user'),
      enableHiding: false,
      cell: ({ row }) => {
        const session = row.original;
        return (
          <div className="text-s flex translate-y-px transform flex-col font-medium">
            <span className="block truncate leading-normal">{getUserDisplayName(session)}</span>
            <span className="text-xs">&nbsp;</span>
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'analytics.fields.user' },
    }),
    columnHelper.accessor((row) => parseTimestamp(row.start_time)?.getTime() ?? 0, {
      id: 'date',
      header: () => t('common.date'),
      cell: ({ row }) => {
        const session = row.original;
        const { date, time } = formatSessionDateTime(session.start_time);

        return (
          <div className="flex flex-col">
            <span>{date}</span>
            <span className="text-xs text-muted-foreground">{time}</span>
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.date' },
    }),
    columnHelper.accessor('total_user_messages', {
      id: 'info',
      header: () => t('common.info'),
      cell: ({ row }) => {
        const session = row.original;
        const duration = formatDuration(session.start_time, session.end_time, t);

        return (
          <div className="flex flex-col">
            <span>
              {session.total_user_messages}{' '}
              {t('common.message_plural', { count: session.total_user_messages })}
            </span>
            <span className="text-xs text-muted-foreground">
              {duration.value} {duration.unit}
            </span>
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.info' },
    }),
    // Environment/Desk column
    columnHelper.accessor((row) => ({ deskId: row.desk_id, channel: row.channel }), {
      id: 'desk',
      header: () => t('common.environment'),
      cell: ({ getValue }) => {
        const { deskId, channel } = getValue() as { deskId: string; channel: IntegrationType };

        const desk = desks?.find((d) => d.desk_id === deskId);
        const Icon = getIntegrationByType(channel)?.Icon;

        return (
          <div className="flex-start flex flex-col items-center gap-2">
            <span className="flex-start flex w-full items-center gap-1">
              {Icon && <Icon size={16} />}
              {Icon && channel && <span className="text-xs">{t(getChannelKey(channel))}</span>}
            </span>
            <span className="flex-start flex w-full items-center gap-1 text-muted-foreground">
              <BoxIcon size={16} />
              {desk?.name || <span className="pl-6">-</span>}
            </span>
          </div>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.environment' },
    }),
    // Attributes column
    columnHelper.accessor(
      (row) => ({ channel: row.channel, isContained: row.is_contained, isCovered: row.is_covered }),
      {
        id: 'attributes',
        header: () => t('analytics.fields.attributes'),
        cell: ({ getValue }) => {
          const attrs = getValue() as { channel: string; isContained: boolean; isCovered: boolean };
          return (
            <div className="flex flex-col items-start gap-1">
              <Badge
                type="secondary"
                flavour={attrs.isCovered ? 'successful' : 'warning'}
                rounded={false}
              >
                {attrs.isCovered
                  ? t('analytics.charts_tooltip.cover')
                  : t('analytics.charts_tooltip.not_cover')}
              </Badge>
              <Badge
                type="secondary"
                flavour={attrs.isContained ? 'successful' : 'warning'}
                rounded={false}
              >
                {attrs.isContained
                  ? t('analytics.charts_tooltip.contain')
                  : t('analytics.charts_tooltip.not_contain')}
              </Badge>
            </div>
          );
        },
        ...COLUMN_PRESETS.MD,
        meta: { preset: 'MD', label: 'analytics.fields.attributes' },
      }
    ),
    // System insight columns
    columnHelper.accessor('goal_achieved', {
      id: 'goal',
      header: () => t('common.goal'),
      cell: ({ getValue }) => {
        const goalAchieved = getValue() as string | null;
        if (goalAchieved === '0' || !goalAchieved) {
          return (
            <InfoTooltip
              content={t('analytics.empty_tooltip', { 0: t('analytics.fields.ai_agent_goal') })}
              size="auto"
            >
              <span className="block w-12 text-start">-</span>
            </InfoTooltip>
          );
        }
        return (
          <InfoTooltip content={getGoalPercentage(goalAchieved, t).tooltip} size="auto">
            <span>
              <Progress
                className="max-w-[82%] bg-secondary"
                value={getGoalPercentage(goalAchieved, t).value}
              />
            </span>
          </InfoTooltip>
        );
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.goal' },
    }),
    columnHelper.accessor('user_sentiment', {
      id: 'sentiment',
      header: () => t('analytics.fields.sentiment'),
      cell: ({ getValue }) => {
        const user_sentiment = getValue() as string | null;
        if (user_sentiment === '0' || !user_sentiment) {
          return (
            <InfoTooltip
              content={t('analytics.empty_tooltip', { 0: t('analytics.fields.user_sentiment') })}
              size="auto"
            >
              <div className="flex w-fit items-center justify-start">-</div>
            </InfoTooltip>
          );
        }

        const sentimentDisplay = getSentimentDisplay(user_sentiment, t);
        if (sentimentDisplay) {
          return (
            <InfoTooltip content={sentimentDisplay.tooltip} size="auto">
              <div className="flex w-fit items-center justify-start">
                <SmileIcon variant={sentimentDisplay.variant} />
              </div>
            </InfoTooltip>
          );
        }
        return <div className="flex items-center justify-start">-</div>;
      },
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'analytics.fields.sentiment' },
    }),

    ...createCsatColumns(t),

    // Conversation summary column
    columnHelper.accessor('conversation_summary', {
      id: 'summary',
      header: () => t('analytics.fields.summary'),
      cell: ({ getValue }) => {
        const summary = getValue() as string | null;
        if (!summary) {
          return (
            <InfoTooltip content={t('analytics.no_summary')} showArrow>
              <span className="block w-fit">-</span>
            </InfoTooltip>
          );
        }
        return (
          <InfoTooltip content={summary} size="large" showArrow>
            {/* whitespace-normal overrides the table cell's nowrap so the clamp can wrap */}
            <p className="line-clamp-2 text-start whitespace-normal">{summary}</p>
          </InfoTooltip>
        );
      },
      ...COLUMN_PRESETS.LG,
      meta: { preset: 'LG', label: 'analytics.fields.summary' },
    }),

    // Dynamic custom insight columns
    ...(allInsights ? createInsightColumns(allInsights) : []),
  ];
}
